// Steekproef beoordelen (W6 ronde 5; research §3.6).
//
// Elke maand twintig gepubliceerde trends (tien dragend, tien achtergrond),
// steeds dezelfde greep binnen de maand. De beheerder geeft per trend één toets
// — goed · twijfel · fout — met een opmerking als hij wil. De uitslag rekent de
// server: dit scherm stuurt alleen de toets en haalt daarna de lijst en de
// meter opnieuw op. Zo staat er nooit een eigen telling naast die van de server.
//
// Een oordeel SCHRIJFT in de gedeelde database (tabel steekproef_oordelen,
// migratie 0032). Een nieuw oordeel vervangt het vorige; niets wordt gewist.
// Eén opslag tegelijk, en de knoppen blijven dicht tot de lijst van de server
// weer binnen is: pas als de rij het nieuwe oordeel draagt, gaan ze open en
// meldt het scherm "Opgeslagen". Zo klikt niemand verder op een rij die nog het
// oude oordeel toont. (Twee herlaadrondes kunnen elkaar niet inhalen:
// useResource negeert een oudere vraag zodra er een nieuwere loopt.) Een
// opmerkingenveld volgt de server na het opslaan van déze rij, of als de bewaarde
// opmerking echt veranderde — nooit na het opslaan van een andere rij, want dan
// zou getypte, nog niet opgeslagen tekst stil verdwijnen.
//
// Een trend die na een herbouw uit de greep viel maar deze maand al beoordeeld
// was, blijft staan mét zijn knoppen, en met de reden van de server eronder:
// zijn oordeel telt mee en de server neemt er nog een nieuw oordeel over aan
// (backend-review ronde 5).
//
// De woorden van de drie toetsen komen van de server (`toetsen[].label`); dit
// scherm heeft er geen eigen lijst van. Alleen icoon en kleur zijn van hier.

import { useEffect, useId, useLayoutEffect, useRef, useState, type ComponentType } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { SteekproefItem, SteekproefResponse, Toets } from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { aantal, datum, noot } from "./BronnenStukken";
import { FoutIcoon, TwijfelIcoon, VinkIcoon } from "./Iconen";
import { invoer, knopLicht } from "./stijl";
import { leesbaar } from "./woorden";

type Toetsen = SteekproefResponse["toetsen"];
type Beoordeel = (
  item: SteekproefItem,
  toets: Toets,
  opmerking: string,
  woord: string,
) => Promise<string | null>;

// Icoon naast het woord (owner-ontwerptaal), en de kleur van de toets zelf —
// dit is het oordeel van de beheerder, geen stoplicht dat het scherm bedenkt.
const TOETS_ICOON: Record<Toets, ComponentType> = {
  goed: VinkIcoon,
  twijfel: TwijfelIcoon,
  fout: FoutIcoon,
};
const TOETS_KLEUR: Record<Toets, string> = {
  goed: color.accentGreen,
  twijfel: color.accentAmber,
  fout: color.accentRed,
};

/** Het woord van de server voor een toets. Het contract stuurt alle drie mee;
 *  ontbreekt er toch één, dan staat de sleutel er kaal — een eigen woord
 *  verzint dit scherm niet. */
function toetsWoord(toetsen: Toetsen, t: Toets): string {
  return toetsen.find((x) => x.sleutel === t)?.label ?? t;
}

/** De lijst zoals hij binnenkwam, met het volgnummer van de vraag die hem
 *  ophaalde. Zo weet het scherm wanneer de ronde ná een oordeel binnen is —
 *  ook als de server precies dezelfde lijst terugstuurt. */
type Binnen = { lijst: SteekproefResponse; ronde: number };

export function SteekproefBeoordelen({ id, onGewijzigd }: { id: string; onGewijzigd: () => void }) {
  const gevraagd = useRef(0);
  const steekproef = useResource<Binnen>(() => {
    const ronde = ++gevraagd.current;
    return api.steekproef().then((lijst) => ({ lijst, ronde }));
  }, []);
  const [bezig, setBezig] = useState<string | null>(null);
  const [melding, setMelding] = useState("");
  // Opgeslagen, maar de lijst van de server is nog niet terug: `na` is de
  // laatste vraag van vóór het opslaan, `melding` wat er daarna gemeld wordt.
  const [wacht, setWacht] = useState<{ na: number; melding: string } | null>(null);
  const kop = useRef<HTMLHeadingElement>(null);
  const kopId = useId();

  // Wie de lijst opent, gaat verder bij de lijst — niet zelf de pagina afzoeken.
  useEffect(() => {
    kop.current?.focus();
  }, []);

  // Pas als een lijst van ná het opslaan binnen is (de rij toont dan het
  // nieuwe oordeel), gaan de knoppen open en klinkt "Opgeslagen". Mislukt het
  // ophalen, dan ook: het oordeel staat wél, en de lijst toont zijn fout.
  const binnen = steekproef.data?.ronde ?? 0;
  useEffect(() => {
    if (!wacht) return;
    if (binnen <= wacht.na && !steekproef.error) return;
    setWacht(null);
    setBezig(null);
    setMelding(wacht.melding);
  }, [wacht, binnen, steekproef.error]);

  const beoordeel: Beoordeel = async (item, toets, opmerking, woord) => {
    setBezig(item.id);
    setMelding("Het oordeel wordt opgeslagen…");
    try {
      const tekst = opmerking.trim();
      await api.steekproefOordeel({ subjectId: item.id, toets, opmerking: tekst || null });
    } catch (err) {
      setBezig(null);
      setMelding("");
      return leesbaar(err, "Het oordeel kon niet worden opgeslagen — probeer het zo nog eens.");
    }
    // De uitslag is die van de server: lijst én meter opnieuw ophalen. De
    // knoppen blijven dicht tot de lijst terug is (het effect hierboven).
    setWacht({ na: gevraagd.current, melding: `Opgeslagen: ${woord} — ${item.titel}.` });
    steekproef.reload();
    onGewijzigd();
    return null;
  };

  const d = steekproef.data?.lijst;
  const ronde = steekproef.data?.ronde ?? 0;
  return (
    <section id={id} aria-labelledby={kopId}>
      <Card style={{ padding: "16px 18px" }}>
        <h3
          id={kopId}
          ref={kop}
          tabIndex={-1}
          style={{ ...sectionLabel, marginBottom: 8, outline: "none" }}
        >
          {d ? `Steekproef beoordelen · ${d.maandNaam}` : "Steekproef beoordelen"}
        </h3>

        {steekproef.error ? (
          <LoadError reason={steekproef.error} onRetry={steekproef.reload} />
        ) : !d ? (
          <EmptyReason reason="de steekproef wordt opgehaald" />
        ) : (
          <>
            <p style={{ fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>{d.uitleg}</p>
            {/* Ook als de greep leeg is, zegt deze zin van de server het al
                ("Nog geen greep.") — dan geen tweede, eigen zin eronder. */}
            <p
              data-uitslag
              style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, marginTop: 8 }}
            >
              {d.uitslag.tekst}
            </p>
            {d.items.length > 0 && (
              <ol
                aria-label="De trends van deze maand"
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "10px 0 0",
                  opacity: steekproef.stale ? 0.6 : 1,
                  transition: "opacity 0.15s",
                }}
              >
                {d.items.map((item, i) => (
                  <SteekproefRij
                    key={item.id}
                    item={item}
                    ronde={ronde}
                    toetsen={d.toetsen}
                    eerste={i === 0}
                    uit={bezig !== null}
                    bezig={bezig === item.id}
                    onBeoordeel={beoordeel}
                  />
                ))}
              </ol>
            )}
          </>
        )}

        {/* Een levend gebied dat er altijd staat: een schermlezer meldt dan
            zowel "wordt opgeslagen" als het resultaat (zelfde afspraak als de
            raming op Kosten & credits). */}
        <div role="status" aria-live="polite" data-oordeel-status>
          {melding && <p style={{ ...noot, color: color.body }}>{melding}</p>}
        </div>
      </Card>
    </section>
  );
}

function SteekproefRij({
  item,
  ronde,
  toetsen,
  eerste,
  uit,
  bezig,
  onBeoordeel,
}: {
  item: SteekproefItem;
  /** Het volgnummer van de lijst die nu staat; verandert bij elke nieuwe lijst. */
  ronde: number;
  toetsen: Toetsen;
  eerste: boolean;
  /** Er loopt ergens in de lijst een opslag: alle knoppen dicht. */
  uit: boolean;
  /** De opslag van déze rij loopt. */
  bezig: boolean;
  onBeoordeel: Beoordeel;
}) {
  // Een eerdere opmerking staat al in het veld: een nieuw oordeel vervangt het
  // vorige, en zo gaat de opmerking niet stilletjes verloren.
  const [tekst, setTekst] = useState(item.opmerking ?? "");
  const [fout, setFout] = useState<string | null>(null);
  const knoppen = useRef<Partial<Record<Toets, HTMLButtonElement | null>>>({});
  const terug = useRef<Toets | null>(null);

  // Het veld volgt de server alleen waar dat niets kost (zie de kop): als de
  // bewaarde opmerking veranderde, en na het opslaan van déze rij — dan staat er
  // de opmerking zoals de server hem bewaarde (bijv. zonder spaties eromheen).
  const eigenOpslag = useRef(false);
  useEffect(() => {
    setTekst(item.opmerking ?? "");
  }, [item.opmerking]);
  useEffect(() => {
    if (!eigenOpslag.current) return;
    eigenOpslag.current = false;
    setTekst(item.opmerking ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij een nieuwe lijst
  }, [ronde]);

  // Tijdens het opslaan stond de knop dicht en raakte hij de focus kwijt. Zodra
  // de rij klaar is, krijgt hij hem terug: een toetsenbordgebruiker gaat verder
  // waar hij was. Een layout-effect: de focus staat er in dezelfde weergave
  // waarin de rij klaar is (zie ConfiguratieInstellingen).
  useLayoutEffect(() => {
    if (bezig || !terug.current) return;
    knoppen.current[terug.current]?.focus();
    terug.current = null;
  }, [bezig]);

  async function klik(toets: Toets) {
    setFout(null);
    terug.current = toets;
    const fout = await onBeoordeel(item, toets, tekst, toetsWoord(toetsen, toets));
    // Gelukt: de volgende lijst van de server zet dit veld op wat hij bewaarde.
    eigenOpslag.current = fout === null;
    setFout(fout);
  }

  const huidig = item.toets;
  return (
    <li
      data-steekproef-item={item.id}
      aria-busy={bezig || undefined}
      style={{
        padding: "12px 0",
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        display: "flex",
        flexWrap: "wrap",
        gap: "8px 18px",
        alignItems: "flex-start",
      }}
    >
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
        <div style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, lineHeight: 1.45 }}>
          {item.titel || <EmptyReason reason="trend zonder titel" />}
        </div>
        <div
          style={{
            fontSize: 12,
            color: color.soft,
            marginTop: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: "2px 8px",
          }}
        >
          <span>{item.categorie ?? <EmptyReason reason="geen categorie" />}</span>
          <span aria-hidden>·</span>
          <span>{item.laagLabel ?? <EmptyReason reason="laag onbekend" />}</span>
          <span aria-hidden>·</span>
          <span>
            {item.bronnen !== null ? (
              aantal(item.bronnen, "bron", "bronnen")
            ) : (
              <EmptyReason reason="aantal bronnen niet bekend" />
            )}
          </span>
        </div>
        <div
          style={{
            marginTop: 7,
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 10px",
            alignItems: "center",
            fontSize: 12,
            color: color.faint,
          }}
        >
          <ToetsBadge toets={huidig} woord={huidig ? toetsWoord(toetsen, huidig) : null} />
          {huidig && (
            <span>
              {`Laatst beoordeeld${item.door ? ` door ${item.door}` : ""}${
                datum(item.op) ? ` op ${datum(item.op)}` : ""
              }`}
            </span>
          )}
          {item.opmerking && <span style={{ color: color.body }}>{`“${item.opmerking}”`}</span>}
        </div>
        {/* Buiten de greep na een herbouw: de zin van de server zegt wat dat
            betekent. De knoppen blijven — de server neemt een nieuw oordeel aan. */}
        {item.buitenGreep && (
          <p
            data-buiten-greep
            style={{ fontSize: 12, color: color.accentAmber, marginTop: 6, lineHeight: 1.5 }}
          >
            {item.reden ?? <EmptyReason reason="valt buiten de greep van deze maand" />}
          </p>
        )}
      </div>

      <div style={{ flex: "1 1 260px", display: "flex", flexDirection: "column", gap: 6 }}>
        <input
          type="text"
          value={tekst}
          disabled={uit}
          maxLength={2000}
          onChange={(e) => setTekst(e.target.value)}
          placeholder="Opmerking (mag leeg blijven)"
          aria-label={`Opmerking bij ${item.titel}`}
          style={{ ...invoer, padding: "7px 10px", fontSize: size.meta }}
        />
        <div
          role="group"
          aria-label={`Oordeel over ${item.titel}`}
          style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
        >
          {toetsen.map((t) => {
            const Icoon = TOETS_ICOON[t.sleutel];
            const gekozen = huidig === t.sleutel;
            return (
              <button
                key={t.sleutel}
                ref={(el) => {
                  knoppen.current[t.sleutel] = el;
                }}
                type="button"
                aria-pressed={gekozen}
                disabled={uit}
                onClick={() => void klik(t.sleutel)}
                style={{
                  ...knopLicht,
                  padding: "6px 12px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: TOETS_KLEUR[t.sleutel],
                  background: gekozen ? color.lineFaint : "none",
                  borderColor: gekozen ? TOETS_KLEUR[t.sleutel] : color.border,
                  cursor: uit ? "default" : "pointer",
                  opacity: uit && !bezig ? 0.5 : 1,
                }}
              >
                <Icoon /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {fout && (
        <p role="alert" style={{ flexBasis: "100%", fontSize: size.meta, color: color.accentRed }}>
          {fout}
        </p>
      )}
    </li>
  );
}

/** De huidige toets als pil met icoon; nog geen toets = dat, in woorden. */
function ToetsBadge({ toets, woord }: { toets: Toets | null; woord: string | null }) {
  const stijl = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: weight.medium,
    background: color.lineFaint,
    border: `1px solid ${color.line}`,
    borderRadius: radius.pill,
    padding: "2px 8px",
    whiteSpace: "nowrap",
  } as const;
  if (!toets) {
    return (
      <span data-toets="geen" style={{ ...stijl, color: color.soft }}>
        nog niet beoordeeld
      </span>
    );
  }
  const Icoon = TOETS_ICOON[toets];
  return (
    <span data-toets={toets} style={{ ...stijl, color: TOETS_KLEUR[toets] }}>
      <Icoon />
      {woord}
    </span>
  );
}
